import type { TranslationKeys } from '@/lib/i18n/en';

/**
 * Subject classification for every Open Academy course, used by the `Course`
 * structured data on both the index and the detail pages.
 *
 * It is a constant and not a per-course field because the catalogue teaches
 * one subject; a column that would hold the same string nine times is a column
 * that eventually holds eight of the same string and one typo. Deliberately
 * untranslated — schema.org `category` is a taxonomy value read by crawlers,
 * not copy shown to a reader.
 */
export const COURSE_CATEGORY = 'AI Customer Support';

export interface Course {
  id: number;
  slug: string;
  title: string;
  description: string;
  longDescription: string;
  image: string;
  duration: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  modules: {
    title: string;
    lessons: {
      title: string;
      duration: string;
    }[];
  }[];
}

const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

/**
 * Build a localized "N weeks" course-length string from the academy dictionary.
 * Keeps the numeric VALUE identical across locales and only converts the glyph
 * to Arabic-Indic digits when the resolved dictionary is Arabic.
 */
function formatWeeks(weeks: number, t: TranslationKeys['academy']): string {
  const label = weeks === 1 ? t.week : t.weeks;
  const isArabic = /[؀-ۿ]/.test(label);
  const value = isArabic
    ? String(weeks).replace(/\d/g, (d) => ARABIC_INDIC_DIGITS[Number(d)])
    : String(weeks);
  return `${value} ${label}`;
}

export function getCourses(t: TranslationKeys['academy']): Course[] {
  return [
    {
      id: 1,
      slug: 'ai-customer-support-fundamentals',
      title: t['AI Customer Support Fundamentals'],
      description:
        t[
          'Start here. Understand the basics of AI, how LLMs work, and how to identify automation opportunities in your support stack.'
        ],
      longDescription:
        t[
          'This comprehensive course is designed to take you from zero to hero in the world of AI customer support. You will learn the foundational concepts of Large Language Models (LLMs), how they differ from traditional chatbots, and how to strategically implement them to reduce ticket volume and improve customer satisfaction.'
        ],
      image: '/images/academy/ai-customer-support-fundamentals.webp',
      duration: formatWeeks(2, t),
      level: t.Beginner as 'Beginner',
      modules: [
        {
          title: t['1. Introduction to AI in Support'],
          lessons: [
            {
              title: t['The Evolution of Customer Service'],
              duration: '10:00',
            },
            { title: t['Understanding LLMs vs. Chatbots'], duration: '15:30' },
            {
              title: t['Identifying Automation Opportunities'],
              duration: '12:45',
            },
          ],
        },
        {
          title: t['2. Setting the Strategy'],
          lessons: [
            { title: t['Defining Success Metrics'], duration: '08:20' },
            {
              title: t['Building a Knowledge Base Strategy'],
              duration: '14:15',
            },
          ],
        },
      ],
    },
    {
      id: 2,
      slug: 'designing-conversational-flows',
      title: t['Designing Conversational Flows'],
      description:
        t[
          'Learn to map out customer journeys and design natural, human-like conversation paths that solve problems without frustration.'
        ],
      longDescription:
        t[
          'Great AI agents need great design. In this course, you will learn the art of conversation design specifically for customer support. We cover how to map user journeys, handle edge cases, and write scripts that feel human, helpful, and concise.'
        ],
      image: '/images/academy/designing-conversational-flows.webp',
      duration: formatWeeks(3, t),
      level: t.Beginner as 'Beginner',
      modules: [
        {
          title: t['1. Principles of Conversation Design'],
          lessons: [
            { title: t['The Gricean Maxims in Support'], duration: '11:00' },
            { title: t['Mapping the Happy Path'], duration: '16:20' },
          ],
        },
        {
          title: t['2. Handling Complexity'],
          lessons: [
            { title: t['Designing for Error Recovery'], duration: '13:45' },
            { title: t['Managing Context and Memory'], duration: '10:30' },
          ],
        },
      ],
    },
    {
      id: 3,
      slug: 'knowledge-base-architecture',
      title: t['Knowledge Architecture'],
      description:
        t[
          'Your AI is only as good as its data. Master the art of structuring your help center and documentation for optimal AI ingestion.'
        ],
      longDescription:
        t[
          'A well-structured knowledge base is the foundation of effective AI support. This course teaches you how to organize, structure, and optimize your documentation for AI consumption, ensuring accurate and helpful responses every time.'
        ],
      image: '/images/academy/knowledge-base-architecture.webp',
      duration: formatWeeks(4, t),
      level: t.Intermediate as 'Intermediate',
      modules: [
        {
          title: t['1. Documentation Best Practices'],
          lessons: [
            { title: t['Structuring Your Knowledge Base'], duration: '12:00' },
            { title: t['Writing for AI Comprehension'], duration: '14:30' },
            { title: t['Metadata and Tagging Strategies'], duration: '10:45' },
          ],
        },
        {
          title: t['2. Optimization Techniques'],
          lessons: [
            { title: t['Semantic Search Optimization'], duration: '15:20' },
            { title: t['Version Control for Docs'], duration: '11:15' },
          ],
        },
      ],
    },
    {
      id: 4,
      slug: 'building-agentic-workflows',
      title: t['Building Agentic Workflows'],
      description:
        t[
          'Go beyond Q&A. Teach your AI to perform actions—process refunds, update subscriptions, and schedule calls autonomously.'
        ],
      longDescription:
        t[
          'Master the art of designing complex AI workflows that handle multi-step tasks autonomously. Learn how to build AI agents that can take real actions in your systems, from processing refunds to updating customer accounts.'
        ],
      image: '/images/academy/building-agentic-workflows.webp',
      duration: formatWeeks(6, t),
      level: t.Advanced as 'Advanced',
      modules: [
        {
          title: t['1. Introduction to Agentic AI'],
          lessons: [
            { title: t['Understanding Agent Capabilities'], duration: '18:00' },
            { title: t['Planning Multi-Step Workflows'], duration: '20:30' },
            { title: t['Security Considerations'], duration: '16:45' },
          ],
        },
        {
          title: t['2. Implementation'],
          lessons: [
            { title: t['Building Your First Agent'], duration: '25:20' },
            { title: t['API Integration Patterns'], duration: '22:15' },
            { title: t['Error Handling and Recovery'], duration: '19:30' },
          ],
        },
      ],
    },
    {
      id: 5,
      slug: 'sentiment-analysis-empathy',
      title: t['Sentiment Analysis & Empathy'],
      description:
        t[
          'Fine-tune your AI to detect customer frustration and respond with appropriate empathy, tone, and escalation paths.'
        ],
      longDescription:
        t[
          'Learn how to build AI systems that understand and respond to customer emotions. This course covers sentiment analysis, empathy modeling, and creating escalation paths for frustrated customers.'
        ],
      image: '/images/academy/sentiment-analysis-empathy.webp',
      duration: formatWeeks(3, t),
      level: t.Intermediate as 'Intermediate',
      modules: [
        {
          title: t['1. Understanding Sentiment'],
          lessons: [
            { title: t['Emotion Detection Fundamentals'], duration: '13:00' },
            { title: t['Building Sentiment Models'], duration: '17:30' },
            { title: t['Tone Analysis'], duration: '11:45' },
          ],
        },
        {
          title: t['2. Empathetic Responses'],
          lessons: [
            { title: t['Crafting Empathetic Messages'], duration: '14:20' },
            { title: t['Escalation Triggers'], duration: '12:15' },
          ],
        },
      ],
    },
    {
      id: 6,
      slug: 'quality-assurance-ai-agents',
      title: t['Quality Assurance for AI Agents'],
      description:
        t[
          'How to monitor, evaluate, and continuously improve your AI agents performance using automated testing and human-in-the-loop review.'
        ],
      longDescription:
        t[
          'Ensure your AI agents maintain high quality standards through systematic testing and evaluation. Learn automated testing strategies, human review processes, and continuous improvement methodologies.'
        ],
      image: '/images/academy/quality-assurance-ai-agents.webp',
      duration: formatWeeks(4, t),
      level: t.Advanced as 'Advanced',
      modules: [
        {
          title: t['1. Testing Strategies'],
          lessons: [
            { title: t['Automated Testing for AI'], duration: '16:00' },
            { title: t['Creating Test Suites'], duration: '18:30' },
            { title: t['Performance Metrics'], duration: '13:45' },
          ],
        },
        {
          title: t['2. Continuous Improvement'],
          lessons: [
            { title: t['Human-in-the-Loop Review'], duration: '15:20' },
            { title: t['Feedback Loops'], duration: '14:15' },
          ],
        },
      ],
    },
    {
      id: 7,
      slug: 'voice-ai-implementation',
      title: t['Voice AI Implementation'],
      description:
        t[
          'The frontier of support. Learn to configure, script, and deploy voice agents that handle phone calls with human-like latency and understanding.'
        ],
      longDescription:
        t[
          'Dive into the cutting edge of AI support with voice technology. Learn how to build and deploy voice AI agents that can handle phone calls with natural conversation flow and minimal latency.'
        ],
      image: '/images/academy/voice-ai-implementation.webp',
      duration: formatWeeks(5, t),
      level: t.Advanced as 'Advanced',
      modules: [
        {
          title: t['1. Voice AI Fundamentals'],
          lessons: [
            { title: t['Speech Recognition Basics'], duration: '17:00' },
            { title: t['Text-to-Speech Systems'], duration: '19:30' },
            { title: t['Latency Optimization'], duration: '15:45' },
          ],
        },
        {
          title: t['2. Building Voice Agents'],
          lessons: [
            { title: t['Conversation Design for Voice'], duration: '21:20' },
            { title: t['Interruption Handling'], duration: '16:15' },
            { title: t['Deployment Strategies'], duration: '18:30' },
          ],
        },
      ],
    },
    {
      id: 8,
      slug: 'multilingual-support-strategy',
      title: t['Multilingual Support Strategy'],
      description:
        t[
          'Scale your support globally without hiring local teams. Master real-time translation pipelines and cultural nuance in AI responses.'
        ],
      longDescription:
        t[
          'Expand your support globally with AI-powered multilingual capabilities. Learn how to implement real-time translation, handle cultural nuances, and maintain quality across languages.'
        ],
      image: '/images/academy/multilingual-support-strategy.webp',
      duration: formatWeeks(3, t),
      level: t.Intermediate as 'Intermediate',
      modules: [
        {
          title: t['1. Multilingual Fundamentals'],
          lessons: [
            { title: t['Translation Technologies'], duration: '14:00' },
            { title: t['Language Detection'], duration: '12:30' },
            { title: t['Cultural Considerations'], duration: '16:45' },
          ],
        },
        {
          title: t['2. Implementation'],
          lessons: [
            { title: t['Building Translation Pipelines'], duration: '18:20' },
            {
              title: t['Quality Assurance Across Languages'],
              duration: '15:15',
            },
          ],
        },
      ],
    },
    {
      id: 9,
      slug: 'ai-ethics-compliance-cs',
      title: t['AI Ethics & Compliance in CS'],
      description:
        t[
          'Navigate the legal and ethical landscape. GDPR, data privacy, bias mitigation, and ensuring your AI agents remain compliant.'
        ],
      longDescription:
        t[
          'Ensure your AI systems are ethical, compliant, and trustworthy. This course covers legal requirements, privacy considerations, bias mitigation, and ethical AI development practices.'
        ],
      image: '/images/academy/ai-ethics-compliance-cs.webp',
      duration: formatWeeks(4, t),
      level: t.Advanced as 'Advanced',
      modules: [
        {
          title: t['1. Legal & Regulatory Framework'],
          lessons: [
            { title: t['GDPR and Data Privacy'], duration: '19:00' },
            { title: t['Compliance Requirements'], duration: '17:30' },
            { title: t['Data Retention Policies'], duration: '14:45' },
          ],
        },
        {
          title: t['2. Ethical AI Development'],
          lessons: [
            { title: t['Bias Detection and Mitigation'], duration: '20:20' },
            { title: t['Transparency and Explainability'], duration: '16:15' },
            { title: t['Accountability Frameworks'], duration: '15:30' },
          ],
        },
      ],
    },
  ];
}

// For backward compatibility - export with English translations
import english from '@/lib/i18n/en';
export const COURSES = getCourses(english.academy);

// Helper function to get course by slug
export function getCourseBySlug(
  slug: string,
  t?: TranslationKeys['academy'],
): Course | undefined {
  const courses = t ? getCourses(t) : COURSES;
  return courses.find((course) => course.slug === slug);
}

// Helper function to get all course slugs (useful for static generation)
export function getAllCourseSlugs(): string[] {
  return [
    'ai-customer-support-fundamentals',
    'designing-conversational-flows',
    'knowledge-base-architecture',
    'building-agentic-workflows',
    'sentiment-analysis-empathy',
    'quality-assurance-ai-agents',
    'voice-ai-implementation',
    'multilingual-support-strategy',
    'ai-ethics-compliance-cs',
  ];
}
